import { Link, useNavigate } from "react-router-dom";
import FlagDropdown from "./FlagDropdown";
import TaramLogo from "./Icons/TaramLogo";
import { useAppContext } from "../context/AppContext";

const Footer = () => {
  const { jwtToken } = useAppContext();
  const navigate = useNavigate();

  const handleBookADemo = () => {
    (
      document.getElementById(
        "book-a-demo-herosection-btn"
      ) as HTMLButtonElement
    ).click();
  };

  return (
    <footer className="bg-[#fafafa] border-t border-gray-200 text-[#2c2c2c]">
      {/* CTA Section */}
      <div className="text-center px-6 py-16 md:px-20 md:py-24 bg-white">
        <h1 className="font-semibold text-3xl md:text-4xl leading-tight max-w-4xl mx-auto text-gray-900">
          Put us to work as your B2B global cross-border trade platform
        </h1>
        <p className="max-w-2xl mx-auto text-[15px] md:text-base text-gray-600 mt-5 leading-relaxed">
          Taram works to optimize your cross-border operations as an exporter or
          importer. Let Taram make your global trade faster, cheaper, and more
          secure — right from the very first click.
        </p>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-10">
          <button
            onClick={() => {
              jwtToken ? navigate("/dashboard") : navigate("/signup");
            }}
            className="border border-gray-800 text-gray-800 hover:bg-gray-900 hover:text-white transition-all w-[200px] h-[50px] rounded-lg font-medium"
          >
            Open an Account
          </button>
          <button
            onClick={handleBookADemo}
            className="bg-custom-blue-medium hover:bg-blue-700 text-white transition-all w-[200px] h-[50px] rounded-lg font-medium"
          >
            Book a Demo
          </button>
        </div>
      </div>

      {/* Footer Bottom Section */}
      <div className="container mx-auto px-6 md:px-20 py-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 border-t border-gray-200 pt-8">
          {/* Left Side - Logo & Rights */}
          <div className="space-y-3">
            <TaramLogo className="w-[150px]" />
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} TARAM. All rights reserved.
            </p>
          </div>

          {/* Middle Section (optional links — uncomment if needed) */}
          {/* <div className="space-y-2 text-gray-600">
            <Link to="#" className="hover:text-gray-900 hover:underline">
              Terms & Policies
            </Link>
            <Link to="#" className="hover:text-gray-900 hover:underline">
              Cookie Preferences
            </Link>
            <Link
              to={adminJWTToken ? "/admin/dashboard" : "/admin/login"}
              className="hover:underline"
            >
              Admin
            </Link>
          </div> */}

          {/* Right Side - Contact & Socials */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="space-y-2 text-gray-600 text-sm">
              <p>manju@taram.io</p>
              <p>Europe: +420 775225229</p>
              <p>USA: +1 316 768 0502</p>
            </div>

            <div className="flex items-center gap-4">
              <a
                href="https://x.com/Taram_ai"
                target="_blank"
                rel="noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <svg
                  width={18}
                  height={18}
                  viewBox="0 0 15 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.59974 7.01649L13.8789 0.879883H12.6279L8.04401 6.20821L4.38286 0.879883H0.160156L5.69654 8.93726L0.160156 15.3725H1.41122L6.25194 9.74554L10.1184 15.3725H14.3411L8.59974 7.01649Z"
                    fill="black"
                  />
                </svg>
              </a>

              <a
                href="https://www.linkedin.com/company/taram-ai"
                target="_blank"
                rel="noreferrer"
                className="hover:opacity-80 transition-opacity"
              >
                <svg
                  width={18}
                  height={18}
                  viewBox="0 0 15 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M12.782 13.4119H10.558V9.93088C10.558 9.10088 10.543 8.03288 9.402 8.03288C8.245 8.03288 8.067 8.93688 8.067 9.87088V13.4119H5.844V6.25288H7.978V7.23188H8.008C8.305 6.66888 9.031 6.07588 10.113 6.07588C12.366 6.07588 12.782 7.55788 12.782 9.48588V13.4119ZM3.337 5.27488C3.16759 5.27488 2.99985 5.24152 2.84334 5.17669C2.68683 5.11186 2.54462 5.01684 2.42483 4.89705C2.30504 4.77726 2.21002 4.63505 2.1452 4.47854C2.08037 4.32203 2.047 4.15429 2.047 3.98488C2.047 3.81548 2.08037 3.64773 2.1452 3.49122C2.21002 3.33471 2.30504 3.1925 2.42483 3.07272C2.54462 2.95293 2.68683 2.85791 2.84334 2.79308C2.99985 2.72825 3.16759 2.69488 3.337 2.69488C3.67913 2.69488 4.00725 2.83079 4.24917 3.07272C4.49109 3.31464 4.627 3.64275 4.627 3.98488C4.627 4.32701 4.49109 4.65513 4.24917 4.89705C4.00725 5.13897 3.67913 5.27488 3.337 5.27488ZM2.224 13.4119H4.449V6.25288H2.224V13.4119ZM13.89 0.629883H1.107C0.497 0.629883 0 1.11388 0 1.70988V14.5479C0 15.1449 0.496 15.6299 1.107 15.6299H13.89C14.502 15.6299 15 15.1449 15 14.5479V1.70988C15 1.11388 14.502 0.629883 13.89 0.629883Z"
                    fill="black"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
